import { SyntheticEvent } from 'react';
import { useTheme } from 'styled-components';
import useRipple from 'use-ripple-hook';

import { SpinningIcon } from '@/components/common/spinning-icon';

import createButtonCSS from './button-css-factory';

export enum ButtonVariant {
  Primary = 'primary',
  Secondary = 'secondary',
  Link = 'link',
}

interface IProps {
  id?: string;

  /** JSX|String component */
  children: React.ReactNode | string;

  /** if the button is disabled, user can't click. */
  disabled?: boolean;

  /** CSS classname, any given class will override existing class.
   * Ex: if 'bg-primary' is an already existing class, you can override it by passing 'bg-secondary'
   */
  classnames?: string;

  /** inline CSS object */
  style?: object;

  /** variant of the button */
  variant?: ButtonVariant;

  /** when user clicks on the button, function will be called */
  onClick?: (e: SyntheticEvent) => void;

  /** type of the button */
  type?: 'button' | 'submit' | 'reset';

  /** if isLoading is true, button will have a spining animation & disabled */
  isLoading?: boolean;
}

const loadingButtonContent = () => {
  return (
    <>
      <SpinningIcon />
      <span>Loading...</span>
    </>
  );
};

export const Button: React.FC<IProps> = ({
  children,
  classnames,
  variant = ButtonVariant.Primary,
  type = 'button',
  isLoading,
  disabled = false,
  ...props
}) => {
  // custom hook to add an animation (ripple effect) to the button
  const [ripple, event] = useRipple({
    duration: 450,
    color: 'rgba(255, 255, 255, .6)',
    disabled,
  });

  // custom hook to extract the current theme from Theme context
  const t: any = useTheme();
  const theme = t.current;

  const isDisabled = disabled || isLoading;

  // CSS classes will be managed here based on the application state
  const css = createButtonCSS({
    theme,
    variant,
    classnames,
    disabled: isDisabled,
  });

  return (
    <button
      {...props}
      disabled={isDisabled}
      ref={ripple}
      type={type}
      className={css}
      onMouseDown={event}
    >
      {isLoading ? loadingButtonContent() : children}
    </button>
  );
};
